import {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useMemo,
} from 'react';
import { type ContextInterface } from '@convertcom/js-sdk';

import convertSdk from '../sdk/convertSdkClient';

import { useUserContext } from './UserContext';
import { useAuthOptions } from '../hooks/useAuthOptions';

import { PAGE } from '../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';

type UseConvertContextParameters = { page: PAGE };

type ConvertProviderParameters = { children: ReactNode };

const ConvertContext = createContext<{
  sdkContext: ContextInterface | null | undefined;
} | null>(null);
/**
 * This function allows the Convert SDK context to be accessed anywhere within the application
 * in order to run A/B experiments that require code changes.
 *
 * This may include displaying different components and executing different functions
 * depending on variations within the experiment.
 */
const ConvertProvider = (params: ConvertProviderParameters) => {
  const { children } = params;

  const [sdkContext, setSdkContext] = useState<
    ContextInterface | null | undefined
  >();
  const {
    consentOptions: { allowConvert },
  } = useUserContext();

  const { user } = useAuthOptions();

  useEffect(() => {
    const initSdk = async (userId: string) => {
      try {
        await convertSdk.onReady();
        const context = convertSdk.createContext(userId);
        setSdkContext(context);
      } catch (error) {
        setSdkContext(null);
        logger.error({
          error,
          message: 'Error loading Convert SDK',
          tags: [PAGE.APP],
        });
      }
    };
    if (
      allowConvert &&
      typeof user?.id === 'string' &&
      user.id.trim().length > 0
    ) {
      initSdk(user.id);
    } else {
      setSdkContext(null);
    }
  }, [allowConvert, user?.id]);

  const contextValue = useMemo(
    () => ({
      sdkContext,
    }),
    [sdkContext],
  );

  return (
    <ConvertContext.Provider value={contextValue}>
      {children}
    </ConvertContext.Provider>
  );
};

const useConvertContext = (params: UseConvertContextParameters) => {
  const { page } = params;
  const context = useContext(ConvertContext);
  if (!context) {
    const error = new Error(
      'useConvertContext must be used within an instance of ConvertProvider',
    );
    logger.error({
      error,
      message:
        'useConvertContext must be used within an instance of ConvertProvider',
      tags: [page],
    });
    throw error;
  }
  return context;
};

export { ConvertProvider, useConvertContext, ConvertContext };
